"use client";

// packages/react/presence/src/Presence.tsx
import _slicedToArray from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import * as React2 from "react";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { useLayoutEffect } from "@radix-ui/react-use-layout-effect";

// packages/react/presence/src/useStateMachine.tsx
import * as React from "react";
function useStateMachine(initialState, machine) {
  return React.useReducer(function (state, event) {
    var nextState = machine[state][event];
    return nextState !== null && nextState !== void 0 ? nextState : state;
  }, initialState);
}

// packages/react/presence/src/Presence.tsx
var Presence = function Presence(props) {
  var present = props.present,
    children = props.children;
  var presence = usePresence(present);
  var child = typeof children === "function" ? children({
    present: presence.isPresent
  }) : React2.Children.only(children);
  var ref = useComposedRefs(presence.ref, getElementRef(child));
  var forceMount = typeof children === "function";
  return forceMount || presence.isPresent ? React2.cloneElement(child, {
    ref: ref
  }) : null;
};
Presence.displayName = "Presence";
function usePresence(present) {
  var _React2$useState = React2.useState(),
    _React2$useState2 = _slicedToArray(_React2$useState, 2),
    node = _React2$useState2[0],
    setNode = _React2$useState2[1];
  var stylesRef = React2.useRef({});
  var prevPresentRef = React2.useRef(present);
  var prevAnimationNameRef = React2.useRef("none");
  var initialState = present ? "mounted" : "unmounted";
  var _useStateMachine = useStateMachine(initialState, {
      mounted: {
        UNMOUNT: "unmounted",
        ANIMATION_OUT: "unmountSuspended"
      },
      unmountSuspended: {
        MOUNT: "mounted",
        ANIMATION_END: "unmounted"
      },
      unmounted: {
        MOUNT: "mounted"
      }
    }),
    _useStateMachine2 = _slicedToArray(_useStateMachine, 2),
    state = _useStateMachine2[0],
    send = _useStateMachine2[1];
  React2.useEffect(function () {
    var currentAnimationName = getAnimationName(stylesRef.current);
    prevAnimationNameRef.current = state === "mounted" ? currentAnimationName : "none";
  }, [state]);
  useLayoutEffect(function () {
    var styles = stylesRef.current;
    var wasPresent = prevPresentRef.current;
    var hasPresentChanged = wasPresent !== present;
    if (hasPresentChanged) {
      var prevAnimationName = prevAnimationNameRef.current;
      var currentAnimationName = getAnimationName(styles);
      if (present) {
        send("MOUNT");
      } else if (currentAnimationName === "none" || (styles === null || styles === void 0 ? void 0 : styles.display) === "none") {
        send("UNMOUNT");
      } else {
        var isAnimating = prevAnimationName !== currentAnimationName;
        if (wasPresent && isAnimating) {
          send("ANIMATION_OUT");
        } else {
          send("UNMOUNT");
        }
      }
      prevPresentRef.current = present;
    }
  }, [present, send]);
  useLayoutEffect(function () {
    if (node) {
      var _node$ownerDocument$d;
      var timeoutId;
      var ownerWindow = (_node$ownerDocument$d = node.ownerDocument.defaultView) !== null && _node$ownerDocument$d !== void 0 ? _node$ownerDocument$d : window;
      var handleAnimationEnd = function handleAnimationEnd(event) {
        var currentAnimationName = getAnimationName(stylesRef.current);
        var isCurrentAnimation = currentAnimationName.includes(event.animationName);
        if (event.target === node && isCurrentAnimation) {
          send("ANIMATION_END");
          if (!prevPresentRef.current) {
            var currentFillMode = node.style.animationFillMode;
            node.style.animationFillMode = "forwards";
            timeoutId = ownerWindow.setTimeout(function () {
              if (node.style.animationFillMode === "forwards") {
                node.style.animationFillMode = currentFillMode;
              }
            });
          }
        }
      };
      var handleAnimationStart = function handleAnimationStart(event) {
        if (event.target === node) {
          prevAnimationNameRef.current = getAnimationName(stylesRef.current);
        }
      };
      node.addEventListener("animationstart", handleAnimationStart);
      node.addEventListener("animationcancel", handleAnimationEnd);
      node.addEventListener("animationend", handleAnimationEnd);
      return function () {
        ownerWindow.clearTimeout(timeoutId);
        node.removeEventListener("animationstart", handleAnimationStart);
        node.removeEventListener("animationcancel", handleAnimationEnd);
        node.removeEventListener("animationend", handleAnimationEnd);
      };
    } else {
      send("ANIMATION_END");
    }
  }, [node, send]);
  return {
    isPresent: ["mounted", "unmountSuspended"].includes(state),
    ref: React2.useCallback(function (node2) {
      if (node2) stylesRef.current = getComputedStyle(node2);
      setNode(node2);
    }, [])
  };
}
function getAnimationName(styles) {
  return (styles === null || styles === void 0 ? void 0 : styles.animationName) || "none";
}
function getElementRef(element) {
  var _Object$getOwnPropert, _Object$getOwnPropert2;
  var getter = (_Object$getOwnPropert = Object.getOwnPropertyDescriptor(element.props, "ref")) === null || _Object$getOwnPropert === void 0 ? void 0 : _Object$getOwnPropert.get;
  var mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
  if (mayWarn) {
    return element.ref;
  }
  getter = (_Object$getOwnPropert2 = Object.getOwnPropertyDescriptor(element, "ref")) === null || _Object$getOwnPropert2 === void 0 ? void 0 : _Object$getOwnPropert2.get;
  mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
  if (mayWarn) {
    return element.props.ref;
  }
  return element.props.ref || element.ref;
}
export { Presence };